import type { AgentEvent } from "@/app/state/types";
import { ApiError, type ApiResponse } from "@/shared/data/client";
import { dataEndpoints } from "@/shared/data/endpoints";
import { formatPlatformErrorForDisplay } from "@/shared/data/platformError";
import { t } from "@/shared/i18n";
import { createCompactId } from "@/shared/utils/compactId";
import { getClientDeviceId } from "@/features/transport/lib/clientDeviceId";

export type WsConnectionStatus =
	| "disconnected"
	| "connecting"
	| "connected"
	| "error";

export type WsAccessTokenRefreshReason = "missing" | "unauthorized";

interface WsRequestFrame {
	frame: "request";
	type: string;
	id: string;
	payload?: unknown;
}

interface WsResponseFrame {
	frame: "response";
	id?: string;
	code?: number | string;
	status?: number;
	msg?: string;
	data?: unknown;
}

interface WsStreamEventFrame {
	type?: string;
	seq?: number;
	payload?: unknown;
	[key: string]: unknown;
}

interface WsStreamFrame {
	frame: "stream";
	id?: string;
	event?: WsStreamEventFrame;
	reason?: string;
	lastSeq?: number;
}

interface WsPushFrame {
	frame: "push";
	type?: string;
	payload?: unknown;
	data?: unknown;
	[key: string]: unknown;
}

interface WsErrorFrame {
	frame: "error";
	id?: string;
	type?: string;
	code?: number | string;
	status?: number;
	msg?: string;
	error?: string;
	data?: unknown;
}

type WsInboundFrame = WsResponseFrame | WsStreamFrame | WsPushFrame | WsErrorFrame;

type PendingRequest = {
	resolve: (value: ApiResponse) => void;
	reject: (reason?: unknown) => void;
	abortHandler?: () => void;
	timer?: ReturnType<typeof setTimeout>;
};

type ActiveStream = {
	onEvent: (event: AgentEvent) => void;
	onFrame?: (raw: string) => void;
	onError?: (err: Error) => void;
	onDone?: (reason: string, lastSeq: number) => void;
	reject: (reason?: unknown) => void;
	abortHandler?: () => void;
	signal?: AbortSignal;
};

export interface WsClientOptions {
	accessToken?: string;
	allowAnonymous?: boolean;
	resolveAccessToken?: (
		reason: WsAccessTokenRefreshReason,
	) => string | Promise<string>;
	onAccessTokenChange?: (accessToken: string) => void;
	onStatusChange?: (status: WsConnectionStatus) => void;
	onPush?: (frame: WsPushFrame) => void;
	connectTimeoutMs?: number;
	heartbeatTimeoutMs?: number;
	reconnectBaseDelayMs?: number;
	reconnectMaxDelayMs?: number;
	reconnectTokenRefreshThreshold?: number;
	healthCheckIntervalMs?: number;
	requestTimeoutMs?: number;
}

// Backend targets a 30s application heartbeat; the client allows about three
// missed beats plus scheduler jitter before it treats the socket as stale.
const WS_SERVER_HEARTBEAT_INTERVAL_MS = 30_000;
const DEFAULT_WS_HEARTBEAT_TIMEOUT_MS =
	WS_SERVER_HEARTBEAT_INTERVAL_MS * 3 + 10_000;
const DEFAULT_WS_HEALTH_CHECK_INTERVAL_MS = 5_000;
const WS_HEARTBEAT_TIMEOUT_CLOSE_CODE = 4000;
const WS_HEARTBEAT_TIMEOUT_CLOSE_REASON = "heartbeat timeout";
const WS_HEARTBEAT_TIMEOUT_MESSAGE = "WebSocket heartbeat timeout";
const WS_TRANSPORT_DISCONNECTED_MESSAGE = "WebSocket transport disconnected";
const WS_TRANSPORT_NOT_CONNECTED_MESSAGE = "WebSocket transport is not connected";
const WS_TRANSPORT_NOT_INITIALIZED_MESSAGE =
	"WebSocket transport is not initialized";

export class WsClientDisconnectedError extends Error {
	code: string;

	constructor(message = WS_TRANSPORT_DISCONNECTED_MESSAGE) {
		super(message);
		this.name = "WsClientDisconnectedError";
		this.code = "WS_DISCONNECTED";
	}
}

export class WsClientRequestTimeoutError extends Error {
	code: string;

	constructor(message = "WebSocket request timeout") {
		super(message);
		this.name = "WsClientRequestTimeoutError";
		this.code = "WS_REQUEST_TIMEOUT";
	}
}

export function isWsTransportError(
	error: unknown,
): error is WsClientDisconnectedError | WsClientRequestTimeoutError {
	return (
		error instanceof WsClientDisconnectedError ||
		error instanceof WsClientRequestTimeoutError
	);
}

export interface WsConnectionErrorOptions {
	appMode?: boolean;
	hasAccessToken?: boolean;
}

type WsFrameIdKind = "wsreq" | "wsstream";

function isObjectRecord(value: unknown): value is Record<string, unknown> {
	return value != null && typeof value === "object";
}

function normalizeWsFrameIdPrefix(kind: WsFrameIdKind): "wsr" | "wss" {
	return kind === "wsreq" ? "wsr" : "wss";
}

export function createWsFrameId(
	kind: WsFrameIdKind,
	nowMs = Date.now(),
): string {
	return createCompactId(normalizeWsFrameIdPrefix(kind), {
		nowMs,
		overflowMessage: "WebSocket request id overflow in the same second",
	});
}

function hasHelpfulWsMessage(message: string): boolean {
	return (
		message.startsWith("Missing access token") ||
		message.startsWith("Missing Access Token") ||
		message.startsWith("WebSocket handshake failed") ||
		message.startsWith(WS_TRANSPORT_NOT_INITIALIZED_MESSAGE) ||
		message.startsWith(t("ws.missingAccessToken.browser")) ||
		message.startsWith(t("ws.missingAccessToken.host")) ||
		message.startsWith(t("ws.handshakeFailed")) ||
		message.startsWith(t("ws.disconnected")) ||
		message.startsWith(t("ws.heartbeatTimeout")) ||
		message.startsWith(t("ws.transportNotInitialized"))
	);
}

function normalizeWsCloseReason(reason: unknown): string {
	return String(reason || "").trim();
}

function isHeartbeatTimeoutClose(
	event?: Pick<CloseEvent, "code" | "reason">,
): boolean {
	const code = Number(event?.code ?? 0);
	const reason = normalizeWsCloseReason(event?.reason).toLowerCase();
	return (
		code === WS_HEARTBEAT_TIMEOUT_CLOSE_CODE &&
		reason === WS_HEARTBEAT_TIMEOUT_CLOSE_REASON
	);
}

function isTransportDisconnectedMessage(message: string): boolean {
	return (
		message === WS_TRANSPORT_DISCONNECTED_MESSAGE ||
		message === WS_TRANSPORT_NOT_CONNECTED_MESSAGE ||
		message.startsWith(`${WS_TRANSPORT_DISCONNECTED_MESSAGE}:`)
	);
}

function createDisconnectErrorFromClose(
	event?: Pick<CloseEvent, "code" | "reason">,
): WsClientDisconnectedError {
	if (isHeartbeatTimeoutClose(event)) {
		return new WsClientDisconnectedError(WS_HEARTBEAT_TIMEOUT_MESSAGE);
	}
	const reason = normalizeWsCloseReason(event?.reason);
	if (reason) {
		return new WsClientDisconnectedError(
			`${WS_TRANSPORT_DISCONNECTED_MESSAGE}: ${reason}`,
		);
	}
	return new WsClientDisconnectedError(WS_TRANSPORT_DISCONNECTED_MESSAGE);
}

function missingAccessTokenMessage(appMode = false): string {
	return appMode
		? t("ws.missingAccessToken.host")
		: t("ws.missingAccessToken.browser");
}

export function isWsConnectionFailure(error: unknown): boolean {
	if (error instanceof WsClientDisconnectedError) {
		return true;
	}
	const message =
		error instanceof Error ? String(error.message || "").trim() : "";
	if (!message) {
		return false;
	}
	return (
		hasHelpfulWsMessage(message) ||
		message === "WebSocket connection failed" ||
		message === WS_HEARTBEAT_TIMEOUT_MESSAGE ||
		isTransportDisconnectedMessage(message) ||
		message === WS_TRANSPORT_NOT_INITIALIZED_MESSAGE
	);
}

export function describeWsConnectionFailure(
	error: unknown,
	options: WsConnectionErrorOptions = {},
): string {
	const appMode = Boolean(options.appMode);
	const hasAccessToken = options.hasAccessToken !== false;
	const rawMessage =
		error instanceof Error
			? String(error.message || "").trim()
			: String(error || "").trim();

	if (!hasAccessToken) {
		return missingAccessTokenMessage(appMode);
	}
	if (!rawMessage) {
		return t("ws.handshakeFailed");
	}
	if (rawMessage === WS_TRANSPORT_NOT_INITIALIZED_MESSAGE) {
		return t("ws.transportNotInitialized");
	}
	if (rawMessage === WS_HEARTBEAT_TIMEOUT_MESSAGE) {
		return t("ws.heartbeatTimeout");
	}
	if (isTransportDisconnectedMessage(rawMessage)) {
		return t("ws.disconnected");
	}
	if (hasHelpfulWsMessage(rawMessage)) {
		return rawMessage;
	}
	if (rawMessage === "WebSocket connection failed") {
		return t("ws.handshakeFailed");
	}
	return rawMessage.startsWith("WebSocket ")
		? rawMessage
		: t("ws.connectionFailedWithMessage", { message: rawMessage });
}

export function toWsConnectionError(
	error: unknown,
	options: WsConnectionErrorOptions = {},
): Error {
	const message = describeWsConnectionFailure(error, options);
	if (error instanceof Error && error.message === message) {
		return error;
	}
	return new Error(message);
}

function buildWsUrl(accessToken = ""): string {
	const protocol =
		window.location.protocol === "https:" ? "wss:" : "ws:";
	const url = new URL(`${protocol}//${window.location.host}/ws`);
	const normalizedToken = String(accessToken || "").trim();
	if (normalizedToken) {
		url.searchParams.set("token", normalizedToken);
	}
	const deviceId = getClientDeviceId();
	if (deviceId) {
		url.searchParams.set("deviceId", deviceId);
	}
	return url.toString();
}

function toApiError(frame: WsErrorFrame | WsResponseFrame): ApiError {
	const display = formatPlatformErrorForDisplay(frame);
	return new ApiError(display.message, {
		status: display.status ?? frame.status ?? null,
		code: display.code || (frame.code ?? null),
		data: frame.data ?? null,
		platformError: display.error,
	});
}

function toApiResponse<T>(frame: WsResponseFrame): ApiResponse<T> {
	const code =
		typeof frame.code === "number"
			? frame.code
			: Number.isFinite(Number(frame.code))
				? Number(frame.code)
				: 0;

	if (code !== 0) {
		throw toApiError(frame);
	}

	return {
		status: typeof frame.status === "number" ? frame.status : 200,
		code,
		msg: typeof frame.msg === "string" ? frame.msg : "ok",
		data: (frame.data ?? null) as T,
	};
}

function toAgentEvent(frameEvent: WsStreamEventFrame): AgentEvent {
	const { payload, ...rest } = frameEvent;
	const payloadRecord = isObjectRecord(payload) ? payload : {};
	return {
		...payloadRecord,
		...rest,
		type: String(frameEvent.type || payloadRecord.type || ""),
		seq:
			typeof frameEvent.seq === "number"
				? frameEvent.seq
				: Number(payloadRecord.seq ?? 0) || undefined,
	} as AgentEvent;
}

export class WsClient {
	private accessToken: string;
	private socket: WebSocket | null = null;
	private connectPromise: Promise<void> | null = null;
	private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
	private healthCheckTimer: ReturnType<typeof setInterval> | null = null;
	private accessTokenRefreshPromise: Promise<string> | null = null;
	private lifecycleVersion = 0;
	private reconnectAttempt = 0;
	private lastSeenAt = 0;
	private expectedClose = false;
	private disposed = false;
	private status: WsConnectionStatus = "disconnected";
	private readonly pendingRequests = new Map<string, PendingRequest>();
	private readonly activeStreams = new Map<string, ActiveStream>();
	private onStatusChange?: (status: WsConnectionStatus) => void;
	private onPush?: (frame: WsPushFrame) => void;
	private readonly connectTimeoutMs: number;
	private readonly heartbeatTimeoutMs: number;
	private readonly reconnectBaseDelayMs: number;
	private readonly reconnectMaxDelayMs: number;
	private readonly reconnectTokenRefreshThreshold: number;
	private readonly healthCheckIntervalMs: number;
	private readonly requestTimeoutMs: number;
	private resolveAccessToken?: (
		reason: WsAccessTokenRefreshReason,
	) => string | Promise<string>;
	private onAccessTokenChange?: (accessToken: string) => void;
	private allowAnonymous: boolean;

	constructor(options: WsClientOptions = {}) {
		this.accessToken = String(options.accessToken || "").trim();
		this.allowAnonymous = Boolean(options.allowAnonymous);
		this.resolveAccessToken = options.resolveAccessToken;
		this.onAccessTokenChange = options.onAccessTokenChange;
		this.onStatusChange = options.onStatusChange;
		this.onPush = options.onPush;
		this.connectTimeoutMs = Math.max(1000, options.connectTimeoutMs ?? 10_000);
		this.heartbeatTimeoutMs = Math.max(
			1000,
			options.heartbeatTimeoutMs ?? DEFAULT_WS_HEARTBEAT_TIMEOUT_MS,
		);
		this.reconnectBaseDelayMs = Math.max(100, options.reconnectBaseDelayMs ?? 1_000);
		this.reconnectMaxDelayMs = Math.max(
			this.reconnectBaseDelayMs,
			options.reconnectMaxDelayMs ?? 30_000,
		);
		this.reconnectTokenRefreshThreshold = Math.max(
			1,
			options.reconnectTokenRefreshThreshold ?? 2,
		);
		this.healthCheckIntervalMs = Math.max(
			1000,
			options.healthCheckIntervalMs ?? DEFAULT_WS_HEALTH_CHECK_INTERVAL_MS,
		);
		this.requestTimeoutMs = Math.max(1, options.requestTimeoutMs ?? 30_000);
	}

	updateOptions(options: Partial<WsClientOptions> = {}): void {
		if (this.disposed) {
			return;
		}
		if (options.accessToken !== undefined) {
			this.accessToken = String(options.accessToken || "").trim();
		}
		if (options.allowAnonymous !== undefined) {
			this.allowAnonymous = Boolean(options.allowAnonymous);
		}
		if (options.resolveAccessToken !== undefined) {
			this.resolveAccessToken = options.resolveAccessToken;
		}
		if (options.onAccessTokenChange !== undefined) {
			this.onAccessTokenChange = options.onAccessTokenChange;
		}
		if (options.onStatusChange !== undefined) {
			this.onStatusChange = options.onStatusChange;
		}
		if (options.onPush !== undefined) {
			this.onPush = options.onPush;
		}
	}

	connect(): Promise<void> {
		if (this.disposed) {
			return Promise.reject(this.createDisposedError());
		}
		return this.ensureConnected();
	}

	dispose(): void {
		if (this.disposed) {
			return;
		}
		this.disposed = true;
		this.cleanupPending(this.createDisposedError());
		this.disconnect();
	}

	disconnect(): void {
		this.lifecycleVersion += 1;
		this.expectedClose = true;
		this.clearReconnectTimer();
		this.clearHealthCheckTimer();
		this.cleanupPending(new WsClientDisconnectedError());

		if (this.socket) {
			try {
				if (
					this.socket.readyState === WebSocket.OPEN ||
					this.socket.readyState === WebSocket.CONNECTING
				) {
					this.socket.close(1000, "ws transport disconnect");
				}
			} catch {
				// Ignore close failures from a half-open socket.
			}
		}

		this.socket = null;
		this.connectPromise = null;
		this.reconnectAttempt = 0;
		this.setStatus("disconnected");
	}

	getStatus(): WsConnectionStatus {
		return this.status;
	}

	async request<T>(opts: {
		type: string;
		payload?: unknown;
		signal?: AbortSignal;
	}): Promise<ApiResponse<T>> {
		await this.ensureConnected(opts.signal);
		const id = createWsFrameId("wsreq");

		return new Promise<ApiResponse<T>>((resolve, reject) => {
			const cleanup = () => {
				const current = this.pendingRequests.get(id);
				if (current?.timer) {
					clearTimeout(current.timer);
				}
				if (current?.abortHandler && opts.signal) {
					opts.signal.removeEventListener("abort", current.abortHandler);
				}
				this.pendingRequests.delete(id);
			};

			const abortHandler = () => {
				cleanup();
				reject(new DOMException("The operation was aborted.", "AbortError"));
			};

			if (opts.signal?.aborted) {
				abortHandler();
				return;
			}

			if (opts.signal) {
				opts.signal.addEventListener("abort", abortHandler, { once: true });
			}

			this.pendingRequests.set(id, {
				resolve: (value) => {
					cleanup();
					resolve(value as ApiResponse<T>);
				},
				reject: (reason) => {
					cleanup();
					reject(reason);
				},
				abortHandler,
				timer: setTimeout(() => {
					cleanup();
					reject(
						new WsClientRequestTimeoutError(
							`WebSocket request timeout: ${opts.type}`,
						),
					);
				}, this.requestTimeoutMs),
			});

			try {
				this.sendFrame({
					frame: "request",
					type: opts.type,
					id,
					payload: opts.payload,
				});
			} catch (error) {
				cleanup();
				reject(error);
			}
		});
	}

	stream(opts: {
		type: string;
		payload: unknown;
		signal?: AbortSignal;
		onEvent: (event: AgentEvent) => void;
		onFrame?: (raw: string) => void;
		onError?: (err: Error) => void;
		onDone?: (reason: string, lastSeq: number) => void;
		requestId?: string;
	}): { requestId: string; abort: () => void } {
		const id = opts.requestId || createWsFrameId("wsstream");
		let aborted = false;

		const abort = () => {
			if (aborted) {
				return;
			}
			aborted = true;
			this.cleanupStream(id, opts.signal);
		};

		const abortHandler = () => {
			abort();
			opts.onError?.(new DOMException("The operation was aborted.", "AbortError"));
		};

		if (opts.signal?.aborted) {
			abortHandler();
			return { requestId: id, abort };
		}

		this.activeStreams.set(id, {
			onEvent: opts.onEvent,
			onFrame: opts.onFrame,
			onError: opts.onError,
			onDone: opts.onDone,
			reject: (reason) => {
				abort();
				if (reason instanceof Error) {
					opts.onError?.(reason);
					return;
				}
				opts.onError?.(new Error(String(reason || "WebSocket stream failed")));
			},
			abortHandler,
			signal: opts.signal,
		});

		if (opts.signal) {
			opts.signal.addEventListener("abort", abortHandler, { once: true });
		}

		void this.ensureConnected(opts.signal)
			.then(() => {
				if (aborted || !this.activeStreams.has(id)) {
					return;
				}
				this.sendFrame({
					frame: "request",
					type: opts.type,
					id,
					payload: opts.payload,
				});
			})
			.catch((error) => {
				abort();
				opts.onError?.(
					error instanceof Error ? error : new Error(String(error || "WebSocket stream failed")),
				);
			});

		return { requestId: id, abort };
	}

	attachRun(
		runId: string,
		agentKey: string,
		lastSeq: number,
		onEvent: (event: AgentEvent) => void,
		onDone?: (reason: string, lastSeq: number) => void,
		signal?: AbortSignal,
	): { requestId: string; abort: () => void } {
			const requestId = createWsFrameId("wsstream");
			const stream = this.stream({
				type: dataEndpoints.attach.path,
			payload: {
				runId,
				agentKey,
				lastSeq,
			},
			signal,
			onEvent,
			onDone,
			onError: (error) => {
				onDone?.(error.name === "AbortError" ? "detached" : "error", 0);
			},
			requestId,
		});
		return {
			requestId,
			abort: stream.abort,
		};
	}

	private async ensureConnected(
		signal?: AbortSignal,
		allowHandshakeRefresh = true,
	): Promise<void> {
		if (this.disposed) {
			throw this.createDisposedError();
		}
		if (this.socket?.readyState === WebSocket.OPEN) {
			return;
		}

		if (signal?.aborted) {
			throw new DOMException("The operation was aborted.", "AbortError");
		}

		if (this.connectPromise) {
			return this.waitForConnection(signal);
		}

		this.expectedClose = false;
		this.setStatus("connecting");
		this.lastSeenAt = Date.now();
		const connectLifecycleVersion = this.lifecycleVersion;
		const isActiveHandshake = () =>
			!this.disposed && this.lifecycleVersion === connectLifecycleVersion;
		const inactiveConnectionError = () =>
			this.disposed
				? this.createDisposedError()
				: new WsClientDisconnectedError("WebSocket connection superseded");

		const pendingConnectPromise = new Promise<void>((resolve, reject) => {
			if (!isActiveHandshake()) {
				reject(inactiveConnectionError());
				return;
			}
			if (!this.accessToken && !this.allowAnonymous) {
				void this.refreshAccessToken("missing")
					.then((token) => {
						if (!isActiveHandshake()) {
							this.connectPromise = null;
							reject(inactiveConnectionError());
							return;
						}
						if (!token) {
							this.connectPromise = null;
							this.setStatus("error");
							reject(
								toWsConnectionError(new Error("Missing access token"), {
									hasAccessToken: false,
								}),
							);
							return;
						}
						this.connectPromise = null;
						void this.ensureConnected(signal, allowHandshakeRefresh)
							.then(resolve)
							.catch(reject);
					})
					.catch((error) => {
						this.connectPromise = null;
						if (!this.disposed) {
							this.setStatus("error");
						}
						reject(error);
					});
				return;
			}

			const socket = new WebSocket(buildWsUrl(this.accessToken));
			this.socket = socket;
			let didRetryHandshakeRefresh = false;
			let connectTimer: ReturnType<typeof setTimeout> | null = setTimeout(() => {
				connectTimer = null;
				cleanupBeforeOpen();
				if (isActiveHandshake() && this.socket === socket) {
					this.socket = null;
					this.setStatus("error");
					this.scheduleReconnect();
				}
				try {
					socket.close(4001, "connect timeout");
				} catch {
					// Ignore close failures for sockets that never finished opening.
				}
				this.connectPromise = null;
				if (!isActiveHandshake()) {
					reject(inactiveConnectionError());
					return;
				}
				reject(
					toWsConnectionError(new Error("WebSocket connection failed"), {
						hasAccessToken: Boolean(this.accessToken),
					}),
				);
			}, this.connectTimeoutMs);

			const cleanupBeforeOpen = () => {
				if (connectTimer) {
					clearTimeout(connectTimer);
					connectTimer = null;
				}
				socket.removeEventListener("open", handleOpen);
				socket.removeEventListener("error", handleError);
				socket.removeEventListener("close", handleCloseBeforeOpen);
			};
			const isCurrentSocket = () => this.socket === socket;

			const retryHandshakeWithFreshToken = async (): Promise<boolean> => {
				if (
					!allowHandshakeRefresh ||
					didRetryHandshakeRefresh ||
					!this.resolveAccessToken ||
					this.expectedClose ||
					!isActiveHandshake() ||
					signal?.aborted
				) {
					return false;
				}
				didRetryHandshakeRefresh = true;
				const previousToken = this.accessToken;
				try {
					await this.refreshAccessToken("unauthorized");
				} catch {
					return false;
				}
				if (!isActiveHandshake()) {
					return false;
				}
				if (!this.accessToken || this.accessToken === previousToken) {
					return false;
				}
				this.connectPromise = null;
				this.socket = null;
				this.setStatus("connecting");
				try {
					await this.ensureConnected(signal, false);
				} catch {
					return false;
				}
				return true;
			};

			const handleOpen = () => {
				cleanupBeforeOpen();
				if (!isActiveHandshake()) {
					try {
						socket.close(1000, "inactive ws connection");
					} catch {
						// Ignore close failures for sockets that are no longer active.
					}
					if (this.socket === socket) {
						this.socket = null;
					}
					reject(inactiveConnectionError());
					return;
				}
				if (!isCurrentSocket()) {
					try {
						socket.close(1000, "stale ws connection");
					} catch {
						// Ignore close failures for sockets that are no longer current.
					}
					if (this.socket?.readyState === WebSocket.OPEN) {
						resolve();
						return;
					}
					resolve();
					return;
				}
				socket.addEventListener("message", this.handleMessage);
				socket.addEventListener("close", this.handleClose);
				socket.addEventListener("error", this.handleSocketError);
				this.lastSeenAt = Date.now();
				this.reconnectAttempt = 0;
				this.clearReconnectTimer();
				this.startHealthCheck();
				this.setStatus("connected");
				resolve();
			};

			const handleError = () => {
				cleanupBeforeOpen();
				const wasCurrentSocket = isCurrentSocket();
				if (wasCurrentSocket) {
					this.socket = null;
				}
				void (async () => {
					if (!isActiveHandshake()) {
						this.connectPromise = null;
						reject(inactiveConnectionError());
						return;
					}
					if (!wasCurrentSocket) {
						if (this.socket?.readyState === WebSocket.OPEN) {
							resolve();
							return;
						}
						resolve();
						return;
					}
					if (await retryHandshakeWithFreshToken()) {
						resolve();
						return;
					}
					if (!isActiveHandshake()) {
						this.connectPromise = null;
						reject(inactiveConnectionError());
						return;
					}
					this.connectPromise = null;
					this.setStatus("error");
					this.scheduleReconnect();
					reject(
						toWsConnectionError(new Error("WebSocket connection failed"), {
							hasAccessToken: Boolean(this.accessToken),
						}),
					);
				})();
			};

			const handleCloseBeforeOpen = (event?: CloseEvent) => {
				cleanupBeforeOpen();
				const wasCurrentSocket = isCurrentSocket();
				void (async () => {
					if (!isActiveHandshake()) {
						this.connectPromise = null;
						reject(inactiveConnectionError());
						return;
					}
					if (!wasCurrentSocket) {
						if (this.socket?.readyState === WebSocket.OPEN) {
							resolve();
							return;
						}
						resolve();
						return;
					}
					if (!this.expectedClose && (await retryHandshakeWithFreshToken())) {
						resolve();
						return;
					}
					if (!isActiveHandshake()) {
						this.connectPromise = null;
						reject(inactiveConnectionError());
						return;
					}
					this.connectPromise = null;
					if (!this.expectedClose) {
						this.setStatus("error");
						this.scheduleReconnect(this.shouldRefreshTokenForClose(event));
					} else {
						this.setStatus("disconnected");
					}
					reject(
						toWsConnectionError(new Error("WebSocket connection failed"), {
							hasAccessToken: Boolean(this.accessToken),
						}),
					);
				})();
			};

			socket.addEventListener("open", handleOpen);
			socket.addEventListener("error", handleError);
			socket.addEventListener("close", handleCloseBeforeOpen);
		});
		let trackedConnectPromise: Promise<void> | null = null;
		trackedConnectPromise = pendingConnectPromise.finally(() => {
			if (trackedConnectPromise && this.connectPromise === trackedConnectPromise) {
				this.connectPromise = null;
			}
		});
		this.connectPromise = trackedConnectPromise;

		return this.waitForConnection(signal);
	}

	private waitForConnection(signal?: AbortSignal): Promise<void> {
		if (!this.connectPromise) {
			return Promise.resolve();
		}
		if (!signal) {
			return this.connectPromise;
		}

		return new Promise<void>((resolve, reject) => {
			const abortHandler = () => {
				signal.removeEventListener("abort", abortHandler);
				reject(new DOMException("The operation was aborted.", "AbortError"));
			};

			if (signal.aborted) {
				abortHandler();
				return;
			}

			signal.addEventListener("abort", abortHandler, { once: true });
			this.connectPromise!
				.then(() => {
					signal.removeEventListener("abort", abortHandler);
					resolve();
				})
				.catch((error) => {
					signal.removeEventListener("abort", abortHandler);
					reject(error);
				});
		});
	}

	private readonly handleMessage = (event: MessageEvent) => {
		this.lastSeenAt = Date.now();
		const raw = typeof event.data === "string" ? event.data : String(event.data);
		let frame: WsInboundFrame;

		try {
			frame = JSON.parse(raw) as WsInboundFrame;
		} catch {
			console.warn(
				"[WsClient] Failed to parse incoming frame:",
				raw.slice(0, 200),
			);
			return;
		}

		if (frame.frame === "response") {
			const pending = frame.id ? this.pendingRequests.get(frame.id) : null;
			if (!pending || !frame.id) {
				return;
			}
			try {
				pending.resolve(toApiResponse(frame));
			} catch (error) {
				pending.reject(error);
			}
			return;
		}

		if (frame.frame === "stream") {
			const stream = frame.id ? this.activeStreams.get(frame.id) : null;
			if (!stream || !frame.id) {
				return;
			}
			stream.onFrame?.(raw);
			if (frame.event) {
				stream.onEvent(toAgentEvent(frame.event));
			}
			if (frame.reason) {
				stream.onDone?.(
					frame.reason,
					typeof frame.lastSeq === "number" ? frame.lastSeq : 0,
				);
				this.cleanupStream(frame.id);
			}
			return;
		}

		if (frame.frame === "push") {
			this.onPush?.(frame);
			return;
		}

		if (frame.frame === "error") {
			const error = toApiError(frame);
			if (frame.id) {
				const pending = this.pendingRequests.get(frame.id);
				if (pending) {
					pending.reject(error);
					return;
				}
				const stream = this.activeStreams.get(frame.id);
				if (stream) {
					stream.reject(error);
					return;
				}
			}
			this.setStatus("error");
		}
	};

	private readonly handleClose = (event?: CloseEvent) => {
		this.clearHealthCheckTimer();
		this.socket?.removeEventListener("message", this.handleMessage);
		this.socket?.removeEventListener("close", this.handleClose);
		this.socket?.removeEventListener("error", this.handleSocketError);
		this.socket = null;
		this.connectPromise = null;

		if (this.disposed || this.expectedClose) {
			this.expectedClose = false;
			this.setStatus("disconnected");
			return;
		}

		this.setStatus("error");
		this.cleanupPending(createDisconnectErrorFromClose(event));
		this.scheduleReconnect(this.shouldRefreshTokenForClose(event));
	};

	private readonly handleSocketError = () => {
		if (this.disposed) {
			return;
		}
		if (this.status !== "connecting") {
			this.setStatus("error");
		}
		if (this.socket?.readyState === WebSocket.OPEN) {
			try {
				this.socket.close(4002, "socket error");
			} catch {
				// Ignore close failures; the close handler will clean up if it fires.
			}
		}
	};

	private sendFrame(frame: WsRequestFrame): void {
		if (this.disposed) {
			throw this.createDisposedError();
		}
		if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
			throw new WsClientDisconnectedError("WebSocket transport is not connected");
		}
		this.socket.send(JSON.stringify(frame));
	}

	private createDisposedError(): WsClientDisconnectedError {
		return new WsClientDisconnectedError("WebSocket client disposed");
	}

	private setStatus(status: WsConnectionStatus): void {
		this.status = status;
		this.onStatusChange?.(status);
	}

	private cleanupPending(error: Error): void {
		for (const [id, pending] of this.pendingRequests.entries()) {
			pending.reject(error);
			this.pendingRequests.delete(id);
		}
		for (const [id, stream] of this.activeStreams.entries()) {
			stream.reject(error);
			this.activeStreams.delete(id);
		}
	}

	private cleanupStream(id: string, signal?: AbortSignal): void {
		const stream = this.activeStreams.get(id);
		if (!stream) {
			return;
		}
		const activeSignal = signal || stream.signal;
		if (stream.abortHandler && activeSignal) {
			activeSignal.removeEventListener("abort", stream.abortHandler);
		}
		this.activeStreams.delete(id);
	}

	private shouldRefreshTokenForClose(
		event?: Pick<CloseEvent, "code" | "reason">,
	): boolean {
		if (isHeartbeatTimeoutClose(event)) {
			return false;
		}
		const reason = normalizeWsCloseReason(event?.reason).toLowerCase();
		const code = Number(event?.code ?? 0);
		return (
			code === 1002 ||
			code === 1006 ||
			code === 1008 ||
			code === 1011 ||
			code >= 4000 ||
			reason.includes("token") ||
			reason.includes("unauthorized") ||
			reason.includes("invalid") ||
			reason.includes("protocol")
		);
	}

	private async refreshAccessToken(
		reason: WsAccessTokenRefreshReason,
	): Promise<string> {
		if (this.disposed) {
			throw this.createDisposedError();
		}
		if (!this.resolveAccessToken) {
			return this.accessToken;
		}
		if (!this.accessTokenRefreshPromise) {
			this.accessTokenRefreshPromise = Promise.resolve(
				this.resolveAccessToken(reason),
			)
				.then((token) => {
					if (this.disposed) {
						throw this.createDisposedError();
					}
					const normalized = String(token || "").trim();
					if (normalized || reason === "unauthorized") {
						this.accessToken = normalized;
						this.onAccessTokenChange?.(normalized);
					}
					return normalized;
				})
				.finally(() => {
					this.accessTokenRefreshPromise = null;
				});
		}
		return this.accessTokenRefreshPromise;
	}

	private scheduleReconnect(forceRefreshToken = false): void {
		if (this.disposed || this.reconnectTimer || this.expectedClose) {
			return;
		}

		const reconnectAttempt = this.reconnectAttempt + 1;
		const shouldRefreshToken =
			Boolean(this.resolveAccessToken) &&
			(forceRefreshToken ||
				reconnectAttempt >= this.reconnectTokenRefreshThreshold);
		const lifecycleVersion = this.lifecycleVersion;
		const delay = Math.min(
			this.reconnectBaseDelayMs * 2 ** this.reconnectAttempt,
			this.reconnectMaxDelayMs,
		);
		this.reconnectAttempt += 1;
		this.reconnectTimer = setTimeout(() => {
			this.reconnectTimer = null;
			if (
				typeof window === "undefined" ||
				typeof WebSocket === "undefined"
			) {
				return;
			}
			void (async () => {
				if (this.lifecycleVersion !== lifecycleVersion || this.expectedClose) {
					return;
				}
				if (this.disposed) {
					return;
				}
				if (shouldRefreshToken) {
					const refreshedToken = await this.refreshAccessToken("unauthorized");
					if (!refreshedToken) {
						this.setStatus("error");
						return;
					}
				}
				if (
					this.disposed ||
					this.lifecycleVersion !== lifecycleVersion ||
					this.expectedClose
				) {
					return;
				}
				await this.connect();
			})().catch((error) => {
				if (isWsConnectionFailure(error)) {
					return;
				}
				console.warn("[WsClient] Reconnect attempt failed:", error);
			});
		}, delay);
	}

	private clearReconnectTimer(): void {
		if (!this.reconnectTimer) {
			return;
		}
		clearTimeout(this.reconnectTimer);
		this.reconnectTimer = null;
	}

	private startHealthCheck(): void {
		this.clearHealthCheckTimer();
		this.healthCheckTimer = setInterval(() => {
			if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
				return;
			}
			if (Date.now() - this.lastSeenAt <= this.heartbeatTimeoutMs) {
				return;
			}
			try {
				this.socket.close(
					WS_HEARTBEAT_TIMEOUT_CLOSE_CODE,
					WS_HEARTBEAT_TIMEOUT_CLOSE_REASON,
				);
			} catch {
				// Ignore close failures and let the socket tear down naturally.
			}
		}, this.healthCheckIntervalMs);
	}

	private clearHealthCheckTimer(): void {
		if (!this.healthCheckTimer) {
			return;
		}
		clearInterval(this.healthCheckTimer);
		this.healthCheckTimer = null;
	}
}
